'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { AlertTriangle, ChevronRight, Loader2, Video, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { useDockCommand } from '@/hooks/useDockCommand';
import { DOCK_CAMERA_LABELS, type DockCameraSlot } from '@/lib/dock-camera';
import {
  dockBayCount,
  dockStatusView,
  dockTypeLabel,
  formatReading,
  hasCapability,
  operationalPhaseTone,
  reading,
  TONE_BADGE,
  TONE_TEXT,
  trayLabelFor,
  type BayAirborne,
  type DockOperationalPhase,
  bayAirborneFrom,
} from '@/lib/dock-status';
import { linkFreshness } from '@/lib/drone-telemetry';
import {
  CONFIRM_DIALOG,
  DOCK_COMMAND_EFFECT,
  DOCK_COMMAND_LABEL,
  DOCK_PHASE_ROW,
  DOCK_PHASE_UNKNOWN,
  bayPositionUnknown,
} from '@/lib/copy';
import { api } from '@/lib/api';
import type {
  DockAckPayload,
  DockBatterySwap,
  DockBay,
  DockCapability,
  DockCommand,
  DockPositionCheck,
  DockPresence,
  DockStatus,
  LiveDockStatus,
} from '@/types/dock';
import { DockBayRow, type LaunchMode } from './DockBayRow';
import { DockPositionBadge } from './DockPositionBadge';

interface DockSidebarProps {
  dockId: string;
  callsign: string | null;
  /** Live WS status; null until the first dock_status arrives. */
  live: LiveDockStatus | null;
  /** Registry fallback, used before/without live data. */
  registryStatus: DockStatus;
  registryIsOnline: boolean;
  capabilities: DockCapability[];
  bays: DockBay[];
  baysError: string | null;
  /** Backend-driven takeoff/landing phase; null = no data reported yet. */
  operationalPhase: DockOperationalPhase | null;
  /**
   * Drones currently flying, mapped to the stamp and altitude of their last telemetry — bay rows
   * show these as IN AIR, qualified by link freshness. The altitude qualifies the LOST case: see
   * `bayAirborneFrom`.
   */
  airborneDrones: ReadonlyMap<string, { lastSeenAt: string; altitudeM: number }>;
  selectedBayDroneId: string | null;
  onSelectBayDrone: (droneId: string | null) => void;
  onLaunch: (droneId: string, mode: LaunchMode) => void;
  /** Center the COP map on an airborne drone. */
  onGoToDrone: (droneId: string) => void;
  subscribeDockAck: (cb: (ack: DockAckPayload) => void) => () => void;
  onClose: () => void;
  /** Which of this dock's cameras already have a window open. The window itself
   *  is not ours: it belongs to the COP page, which is the only surface that
   *  outlives a selection change. All this panel does is offer the verb and show
   *  which ones are already taken. */
  openCameras: readonly DockCameraSlot[];
  onOpenCamera: (cam: DockCameraSlot) => void;
  /** The dock's pin checked against where its aircraft park (`GET /api/docks`); undefined until the registry loads. */
  positionCheck?: DockPositionCheck;
}

/** Commands that change hardware state irreversibly enough to confirm first. */
const CONFIRM_COMMANDS = new Set<DockCommand>([
  'emergency_mode_on',
  'drone_service',
  'rc_power',
  'drone_power_1',
  'drone_power_2',
  'drone_power_3',
  'set_drone_power',
  'turn_off_drones',
]);

/** Bay index → the firmware's per-bay drone power command (migration 015). */
const DRONE_POWER_COMMANDS: Record<number, DockCommand> = {
  1: 'drone_power_1',
  2: 'drone_power_2',
  3: 'drone_power_3',
};

function formatAge(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function Panel({
  title,
  hint,
  children,
}: {
  title: string;
  /** Usually a short status word. Accepts a node so a panel can put a control
   *  here — the header is the only spare row in these dense panels. */
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-subtle bg-popover">
      <header className="flex min-h-9 items-center justify-between border-b divider px-3">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-foreground">{title}</h3>
        {hint && <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{hint}</span>}
      </header>
      <div className="p-2.5">{children}</div>
    </section>
  );
}

/** `numeric`: the value is a reading (a temperature, a wind speed) and takes the
 *  monospace face; a word (Open, Idle, Online) stays in the sans (D17). */
function StatCard({ label, value, valueClass = 'text-foreground', numeric = false }: { label: string; value: string; valueClass?: string; numeric?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-muted/50 px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 text-[11px] font-bold uppercase ${numeric ? 'font-mono tabular-nums' : 'tracking-wider'} ${valueClass}`}>{value}</p>
    </div>
  );
}

/** One manual hardware operation: name, live state, and its action button(s). */
function ControlRow({ name, state, children }: { name: string; state: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-9 items-center gap-2 border-b divider last:border-0">
      <span className="flex-1 text-[11px] text-foreground">{name}</span>
      <span className="text-[10px] uppercase text-muted-foreground">{state}</span>
      {children}
    </div>
  );
}

const ACTION_CLASS =
  'flex h-7 min-w-[4.5rem] items-center justify-center gap-1 rounded border border-border bg-muted/30 px-2 text-[9px] font-bold uppercase text-muted-foreground transition-colors enabled:hover:border-[var(--border-hover)] enabled:hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40';

export function DockSidebar({
  dockId,
  callsign,
  live,
  registryStatus,
  registryIsOnline,
  capabilities,
  bays,
  baysError,
  operationalPhase,
  airborneDrones,
  selectedBayDroneId,
  onSelectBayDrone,
  onLaunch,
  onGoToDrone,
  subscribeDockAck,
  onClose,
  openCameras,
  onOpenCamera,
  positionCheck,
}: DockSidebarProps) {
  const { role } = useAuth();
  const canCommand = role === 'admin' || role === 'operator';

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const [confirmCmd, setConfirmCmd] = useState<DockCommand | null>(null);
  const [envDetailsOpen, setEnvDetailsOpen] = useState(false);

  const status = live?.status ?? registryStatus;
  const isOnline = live?.is_online ?? registryIsOnline;
  const statusView = dockStatusView({
    status,
    isOnline,
    receivedAt: live?.received_at ?? null,
    nowMs: now,
  });

  const command = useDockCommand({ dockId, subscribeDockAck, enabled: canCommand });

  // Hardware actions are refused while the dock cannot receive them — a command
  // published to an offline dock would silently go nowhere.
  const hardwareEnabled = canCommand && isOnline && !statusView.stale && command.submitting === null;
  const hardwareBlockReason = !canCommand
    ? 'Viewer role — commands disabled'
    : !isOnline || statusView.stale
      ? 'Dock offline — hardware actions unavailable'
      : null;

  // Tri-state: true / false / null = the dock has not reported this flag.
  const flag = (value: unknown): boolean | null => {
    const n = reading(value);
    return n === null ? null : n !== 0;
  };
  const emergencyActive = flag(live?.emergency_mode) === true;
  // Tray wire contract (AirScort, confirmed 2026-07-30 - airscort-adapter.ts):
  // 0 closed, 1 OPEN, 2 opening, 3 closing. The transitional states must be
  // visible: launching while the bay is moving (2/3) is a crash, and only the
  // server's go/no-go treats exactly 1 as launch-safe.
  //
  // `trayNotClosed`, not `trayOpen`: it is true for 1, 2 AND 3, and it drives
  // only the readout's tone (never green while the bay is anything but shut).
  // Naming it "open" invited exactly the misreading `deriveStatusEnum` had.
  const trayNotClosed = flag(live?.tray);
  const trayValue = reading(live?.tray);
  const trayLabel = trayValue === null
    ? '—'
    : (trayLabelFor(trayValue) ?? bayPositionUnknown({ value: trayValue }));
  const swapInProgress = flag(live?.swap_battery);
  const batteries = live?.batteries ?? [];
  const supportsBatterySwitch = hasCapability(capabilities, 'battery_switch');

  // THE LAST SWAP, and the only thing on this panel that says anything about the charge of an
  // airframe sitting powered-down in a bay. Fetched rather than pushed: a swap happens once per
  // sortie, so a WebSocket channel for it would carry one message an hour.
  //
  // Refetched when the dock stops reporting `swapping`, which is the moment a new row can exist.
  // Failures are swallowed to null - this is supplementary, and a panel an operator fires
  // emergency commands from must not break because a history read timed out.
  // THE ROW CARRIES THE DOCK IT CAME FROM, and is rendered only when that still matches.
  //
  // Not decoration: `CopDockSheet` is mounted in `page.tsx` with NO `key={dockId}`, so selecting a
  // different dock reuses this component instance and every piece of its state. Held as a bare
  // row, the previous machine's swap would render under the new machine's callsign for as long as
  // the refetch takes - a reading attributed to the wrong dock, which is the exact failure the
  // tray and magazine readers in this file are each written to avoid. Self-identifying state
  // cannot do that, whatever the parent does with keys, and it needs no reset-to-null in the
  // effect body (the cascading render `react-hooks/set-state-in-effect` warns about).
  const [lastSwap, setLastSwap] = useState<{ dockId: string; swap: DockBatterySwap | null } | null>(null);
  useEffect(() => {
    if (!supportsBatterySwitch) return;
    let cancelled = false;
    api.get<{ data: DockBatterySwap[] }>(`/api/docks/${dockId}/swaps`)
      .then((res) => {
        if (!cancelled) setLastSwap({ dockId, swap: res.data[0] ?? null });
      })
      .catch(() => {
        if (!cancelled) setLastSwap({ dockId, swap: null });
      });
    return () => { cancelled = true; };
  }, [dockId, supportsBatterySwitch, swapInProgress]);
  const swapRow = lastSwap?.dockId === dockId ? lastSwap.swap : null;

  const bayByIndex = useMemo(() => {
    const map = new Map<number, DockBay>();
    for (const bay of bays) {
      if (typeof bay.bay_index === 'number') map.set(bay.bay_index, bay);
    }
    return map;
  }, [bays]);

  const bayCount = dockBayCount(capabilities, bays.map((b) => b.bay_index));
  const bayIndices = Array.from({ length: bayCount }, (_, i) => i + 1);

  // Pad occupancy from the dock board (M-001 master frame byte 12, via the
  // adapter). null = this dock's script doesn't report it — stays UNKNOWN.
  const bayPresence: DockPresence =
    live?.drone_on_hub === true ? 'in_dock'
    : live?.drone_on_hub === false ? 'not_in_dock'
    : 'unknown';

  // Flying + how fresh that claim is, against this panel's 1s clock — a drone
  // that stops reporting mid-flight must not keep an unqualified IN AIR chip.
  const bayAirborne = (droneId: string): BayAirborne => {
    const entry = airborneDrones.get(droneId);
    // A lost link is bounded by the last position: silence from an aircraft last seen ON THE PAD
    // is not evidence it is flying, and reading it as such disables its own LAUNCH button for the
    // rest of the session. See `bayAirborneFrom`.
    return entry === undefined
      ? false
      : bayAirborneFrom(linkFreshness(entry.lastSeenAt, now), entry.altitudeM);
  };

  // Persistent, not dismissable: the old default - DJI auto-land in place
  // when the tag is lost - means blind, over the dock structure. An operator
  // must see that configuration before the flight that needs it, not after.

  const receivedAtMs = live ? new Date(live.received_at ?? '').getTime() : NaN;
  const lastKnownAgeMs = Number.isFinite(receivedAtMs) ? now - receivedAtMs : null;
  const staleNotice =
    statusView.stale && lastKnownAgeMs !== null
      ? `Last known · ${formatAge(lastKnownAgeMs)}`
      : null;

  const runCommand = (cmd: DockCommand) => {
    if (!hardwareEnabled) return;
    if (CONFIRM_COMMANDS.has(cmd)) {
      setConfirmCmd(cmd);
      return;
    }
    void command.send(cmd);
  };

  /** pending → ACK → done ✓ / failed ✗ for one command, or nothing in flight. */
  const ackGlyph = (cmd: DockCommand) => {
    const phase = command.phaseOf(cmd);
    if (phase === null) return null;
    if (phase === 'pending') return <Loader2 className="size-3 animate-spin" aria-label="Awaiting dock ack" />;
    if (phase === 'acked') return <span className="text-status-amber-fg">ACK</span>;
    if (phase === 'done') return <span className="text-status-green-fg">✓</span>;
    return (
      <span className="text-status-red-fg" title={command.failureOf(cmd) ?? undefined}>
        ✗
      </span>
    );
  };

  return (
    <div
      data-testid="dock-sidebar"
      // [&>*]:shrink-0 — without it the flex children are compressed to fit
      // instead of scrolling, which clips readings (battery percentages first).
      className={`flex h-full min-h-0 flex-col gap-2 overflow-y-auto overflow-x-hidden p-2 [&>*]:shrink-0 ${
        emergencyActive ? 'bg-status-red/5' : ''
      }`}
    >
      {/* ---- Header: identity, overall status, close ---- */}
      <section
        className={`rounded-lg border p-3 ${
          emergencyActive ? 'border-status-red/30 bg-status-red/5' : 'border-subtle bg-popover'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="callsign truncate text-[15px] text-foreground">
              {callsign ?? dockId.slice(-6).toUpperCase()}
            </p>
            <p className="mt-1 text-[9px] uppercase tracking-wide text-muted-foreground">
              <span className={isOnline && !statusView.stale ? 'text-status-green-fg' : 'text-status-red-fg'}>
                ● {isOnline && !statusView.stale ? 'Online' : 'Offline'}
              </span>
              {lastKnownAgeMs !== null && <> · Updated {formatAge(lastKnownAgeMs)}</>}
            </p>
            <p className="mt-1 text-[9px] uppercase tracking-wide text-muted-foreground">
              {dockTypeLabel(capabilities)}
            </p>
          </div>
          <div className="flex shrink-0 items-start gap-2">
            <span
              data-testid="dock-status-badge"
              className={`rounded border px-2 py-1 text-[9px] font-bold uppercase ${TONE_BADGE[statusView.tone]}`}
            >
              {statusView.label}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dock sidebar"
              className="grid size-6 place-items-center rounded border border-border bg-muted/30 text-muted-foreground transition-colors hover:border-[var(--border-hover)] hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>

        {/* The pin against where this dock's aircraft park - what returns and landings aim at. Full
            width under the identity row, so its sentence is not squeezed beside the status badge. */}
        <div className="mt-2">
          <DockPositionBadge check={positionCheck} />
        </div>

        {/* Operational phase — separate concept from dock status. */}
        <div
          data-testid="dock-operational-phase"
          className={`mt-2.5 rounded-md border px-2.5 py-2 ${TONE_BADGE[operationalPhaseTone(operationalPhase)]}`}
        >
          <p className="text-[9px] uppercase tracking-wider opacity-80">Dock Operational Phase</p>
          <p className="mt-1 text-[12px] font-bold uppercase">
            {operationalPhase ?? DOCK_PHASE_UNKNOWN}
          </p>
        </div>
      </section>

      {/* ---- Emergency: always first, always reachable. BOTH actions are
           always offered: the old toggle trusted the board's reported state,
           and a board that never latches emergency_mode (both site boards
           report 0) made CLEAR unreachable while the dock sat in e-stop. ---- */}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => runCommand('emergency_mode_on')}
          disabled={!hardwareEnabled}
          title={hardwareBlockReason ?? undefined}
          className={`flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border text-[11px] font-bold uppercase tracking-wide transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            emergencyActive
              ? 'border-status-red/60 bg-status-red/15 text-status-red-fg enabled:hover:bg-status-red/25'
              : 'border-status-red/40 bg-status-red/10 text-status-red-fg enabled:hover:bg-status-red/15'
          }`}
        >
          <AlertTriangle className="size-3.5" strokeWidth={2} aria-hidden />
          Emergency
          {ackGlyph('emergency_mode_on')}
        </button>
        <button
          type="button"
          onClick={() => runCommand('emergency_mode_clear')}
          disabled={!hardwareEnabled}
          title={hardwareBlockReason ?? undefined}
          className="flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-status-green/40 bg-status-green/10 text-[11px] font-bold uppercase tracking-wide text-status-green-fg transition-colors enabled:hover:bg-status-green/15 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Clear Emergency
          {ackGlyph('emergency_mode_clear')}
        </button>
      </div>

      {/* ---- Live dock status ---- */}
      <Panel title="Dock Status" hint={staleNotice ?? 'Live'}>
        {live === null ? (
          <p className="text-[10px] uppercase text-muted-foreground">
            No dock status received yet
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-1.5">
              <StatCard
                label="Bay"
                value={trayLabel}
                valueClass={
                  trayNotClosed === null
                    ? TONE_TEXT.neutral
                    : trayNotClosed ? TONE_TEXT.amber : TONE_TEXT.green
                }
              />
              {supportsBatterySwitch && (
                <StatCard
                  label="Battery Swap"
                  value={swapInProgress === null ? '—' : swapInProgress ? 'In progress' : 'Idle'}
                  valueClass={swapInProgress ? TONE_TEXT.amber : 'text-foreground'}
                />
              )}
              <StatCard
                label="Connection"
                value={statusView.stale ? 'Offline' : 'Online'}
                valueClass={statusView.stale ? TONE_TEXT.red : TONE_TEXT.green}
              />
            </div>

            {/* A charger dock has no magazine - it charges the one pack already
                in the drone, not five removable slots - so this grid only
                applies to a battery_switch dock. Same gate as the "Battery
                Swap" stat card above and the Advanced Options swap button
                below; showing it here for a charger drew five EMPTY boxes
                for a bay that was never going to hold packs at all. */}
            {supportsBatterySwitch && (
              <>
                <p className="mb-1.5 mt-2.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                  Batteries
                </p>
                <div className="grid grid-cols-5 gap-1">
                  {batteries.map((b) => (
                    <div
                      key={b.slot}
                      className="rounded-md border border-border bg-muted/50 px-1 py-1.5 text-center"
                    >
                      <span className="block text-[9px] text-muted-foreground">{b.slot}</span>
                      {/* null = EMPTY SLOT, and it says so in the word `DockPanel`
                          already uses. Never rendered as 0% - and never as `—`
                          either, which is this panel's glyph for a reading the dock
                          did not report (`formatReading`). An empty magazine is a
                          measurement, not a gap, and since #548 it is one the
                          platform refuses on: with five dashes beside an amber
                          CHARGING badge, the honest reading of this panel was
                          "percentages not in yet", which is the "waiting will fix
                          it" conclusion the alert exists to deny. */}
                      <span
                        className={`mt-1 block font-mono text-[11px] font-bold tabular-nums ${
                          reading(b.percent) === null ? 'text-muted-foreground' : 'text-foreground'
                        }`}
                      >
                        {reading(b.percent) === null ? 'EMPTY' : formatReading(b.percent, { unit: '%' })}
                      </span>
                    </div>
                  ))}
                  {batteries.length === 0 && (
                    <p className="col-span-5 text-[10px] uppercase text-muted-foreground">
                      No battery data reported
                    </p>
                  )}
                </div>

                {/* LAST SWAP - what the dock actually put in the aircraft.
                    The one reading on this panel about an airframe that is powered down and
                    therefore reporting nothing: after a swap its own `last_telemetry` still holds
                    the flat pack it landed on, and only the magazine's difference says otherwise.
                    Measured on prod 2026-09-14, DJI-2 read 8% in the row while sitting on a pack
                    the dock had just fitted.
                    Shown only once a swap has been watched - a dock with no history renders
                    nothing rather than an empty row promising a number that is not coming. */}
                {swapRow && (
                  <>
                    <p className="mb-1.5 mt-2.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                      Last Swap
                    </p>
                    <div className="rounded-md border border-border bg-muted/50 px-2 py-1.5">
                      {swapRow.outcome === 'derived' && swapRow.fitted_percent !== null ? (
                        <>
                          <p className="font-mono text-[11px] font-bold tabular-nums text-foreground">
                            FITTED {swapRow.fitted_percent}%
                            {swapRow.returned_percent !== null && (
                              <span className="ml-2 font-normal text-muted-foreground">
                                RETURNED {swapRow.returned_percent}%
                              </span>
                            )}
                          </p>
                          <p className="mt-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">
                            {swapRow.ended_at ? formatAge(now - new Date(swapRow.ended_at).getTime()) : '—'}
                            {swapRow.fitted_from_slot !== null && ` · from slot ${swapRow.fitted_from_slot}`}
                          </p>
                        </>
                      ) : swapRow.outcome === 'observing' ? (
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Swap in progress
                        </p>
                      ) : (
                        /* Every other outcome means the same operationally: this platform cannot
                           say what the aircraft is holding. `no_change` and `unobserved` go
                           further and mean it was not resupplied at all, which is why each raises
                           a critical of its own - the panel does not have to shout it twice. */
                        <>
                          <p className="text-[10px] font-bold uppercase tracking-wide text-warning">
                            {swapRow.outcome === 'no_change' || swapRow.outcome === 'unobserved'
                              ? 'Not resupplied'
                              : 'Charge not determined'}
                          </p>
                          {swapRow.evidence?.reason && (
                            <p className="mt-0.5 text-[9px] leading-snug text-muted-foreground">
                              {swapRow.evidence.reason}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Panel>

      {/* ---- Drone bays ---- */}
      {/* The panel that shows which airframe is in which bay is where an
          operator realises one is wrong, so it is also where the way to fix it
          belongs. Without this the only route is Fleet > Docks, an unlabelled
          pencil on the dock's row, then the Bays tab inside the editor — three
          hops, none of them signposted from here. */}
      <Panel
        title="Drone Bays"
        hint={
          <Link
            href={`/fleet/docks?dock=${dockId}&tab=bays`}
            className="rounded text-[9px] uppercase tracking-wide text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            Edit bays
          </Link>
        }
      >
        {baysError && (
          <p className="mb-1.5 text-[10px] text-status-red-fg">{baysError}</p>
        )}
        <div className="flex flex-col gap-1.5">
          {bayIndices.map((index) => {
            const bay = bayByIndex.get(index) ?? null;
            return (
              <DockBayRow
                key={index}
                bayIndex={index}
                bay={bay}
                presence={bayPresence}
                airborne={bay === null ? false : bayAirborne(bay.drone_id)}
                selected={bay !== null && bay.drone_id === selectedBayDroneId}
                canLaunch={canCommand && isOnline && !statusView.stale}
                launchBlockReason={hardwareBlockReason}
                onToggleSelect={() =>
                  bay && onSelectBayDrone(bay.drone_id === selectedBayDroneId ? null : bay.drone_id)
                }
                onLaunch={(mode) => bay && onLaunch(bay.drone_id, mode)}
                onGoToDrone={() => bay && onGoToDrone(bay.drone_id)}
              />
            );
          })}
        </div>
      </Panel>

      {/* ---- Advanced options: manual hardware operations ---- */}
      <details className="overflow-hidden rounded-lg border border-subtle bg-popover [&[open]_.drawer-arrow]:rotate-90">
        <summary className="flex min-h-9 cursor-pointer list-none items-center justify-between px-3 [&::-webkit-details-marker]:hidden">
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-foreground">
            Advanced Options
          </h3>
          <ChevronRight className="drawer-arrow size-4 text-muted-foreground transition-transform" aria-hidden />
        </summary>

        <div className="border-t divider p-2.5">
          {hardwareBlockReason && (
            <p className="mb-2 text-[10px] text-muted-foreground">{hardwareBlockReason}</p>
          )}

          <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            Dock Controls
          </p>
          {/* Both commands are always offered, like the LED pair below. The old
              state-derived toggle made CLOSE unreachable on boards that never
              report tray state (M-011's site firmware always claims closed) —
              the operator's eyes/cameras outrank the board's claim. */}
          <ControlRow name="Bay" state={trayLabel}>
            <button
              type="button"
              onClick={() => runCommand('tray_open')}
              disabled={!hardwareEnabled}
              className={ACTION_CLASS}
            >
              Open
              {ackGlyph('tray_open')}
            </button>
            <button
              type="button"
              onClick={() => runCommand('tray_close')}
              disabled={!hardwareEnabled}
              className={ACTION_CLASS}
            >
              Close
              {ackGlyph('tray_close')}
            </button>
          </ControlRow>

          {/* The dock status payload carries no LED field, so no state is claimed. */}
          <ControlRow name="LED" state="—">
            <button
              type="button"
              onClick={() => runCommand('led_on')}
              disabled={!hardwareEnabled}
              className={ACTION_CLASS}
            >
              On
              {ackGlyph('led_on')}
            </button>
            <button
              type="button"
              onClick={() => runCommand('led_off')}
              disabled={!hardwareEnabled}
              className={ACTION_CLASS}
            >
              Off
              {ackGlyph('led_off')}
            </button>
          </ControlRow>

          {/* AirScort flight-phase commands — the dock side of launch/recovery.
              pre_takeoff extends the bay + powers the drone; post_land closes
              and runs the battery swap on swap docks (field-confirmed
              2026-08-10: swap is post_land, NOT drone_service). The launch
              sequence drives these itself; these buttons exist so the operator
              can exercise the board directly for testing.

              The four are named for their WIRE VERB - PRE-TAKEOFF, not
              "Before takeoff" - because the person pressing one is matching it
              against an MQTT log or docs/GOLDEN-FLOW.md, which say pre_takeoff.
              #528 renamed them by moment; reverted by the founder's call on
              2026-09-05. Never "Arm", which is the drone's own signed verb.
              Words in lib/copy/commands.ts, with a hint for the two phases this
              repo can actually vouch for. */}
          <ControlRow name={DOCK_PHASE_ROW.takeoff} state="—">
            <button
              type="button"
              onClick={() => runCommand('pre_takeoff')}
              disabled={!hardwareEnabled}
              className={ACTION_CLASS}
              title={DOCK_COMMAND_EFFECT.pre_takeoff}
            >
              {DOCK_COMMAND_LABEL.pre_takeoff}
              {ackGlyph('pre_takeoff')}
            </button>
            <button
              type="button"
              onClick={() => runCommand('post_takeoff')}
              disabled={!hardwareEnabled}
              className={ACTION_CLASS}
            >
              {DOCK_COMMAND_LABEL.post_takeoff}
              {ackGlyph('post_takeoff')}
            </button>
          </ControlRow>

          <ControlRow name={DOCK_PHASE_ROW.landing} state="—">
            <button
              type="button"
              onClick={() => runCommand('pre_land')}
              disabled={!hardwareEnabled}
              className={ACTION_CLASS}
            >
              {DOCK_COMMAND_LABEL.pre_land}
              {ackGlyph('pre_land')}
            </button>
            <button
              type="button"
              onClick={() => runCommand('post_land')}
              disabled={!hardwareEnabled}
              className={ACTION_CLASS}
              title={DOCK_COMMAND_EFFECT.post_land}
            >
              {DOCK_COMMAND_LABEL.post_land}
              {ackGlyph('post_land')}
            </button>
          </ControlRow>

          {supportsBatterySwitch && (
            <ControlRow
              name="Battery Swap"
              state={swapInProgress === null ? '—' : swapInProgress ? 'In progress' : 'Idle'}
            >
              <button
                type="button"
                onClick={() => runCommand('drone_service')}
                disabled={!hardwareEnabled || swapInProgress === true}
                className={ACTION_CLASS}
              >
                Start
                {ackGlyph('drone_service')}
              </button>
            </ControlRow>
          )}

          <p className="mb-1 mt-3 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            Power
          </p>
          <p className="mb-2 text-[9px] leading-relaxed text-muted-foreground">
            RC and drone power are driven automatically by the takeoff workflow; these are the manual
            overrides.
          </p>
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={() => runCommand('rc_power')}
              disabled={!hardwareEnabled}
              className={`${ACTION_CLASS} w-full`}
            >
              RC Power
              {ackGlyph('rc_power')}
            </button>
            {bays
              .filter((bay) => typeof bay.bay_index === 'number' && DRONE_POWER_COMMANDS[bay.bay_index])
              .map((bay) => {
                const cmd = DRONE_POWER_COMMANDS[bay.bay_index as number] as DockCommand;
                return (
                  <button
                    key={bay.drone_id}
                    type="button"
                    onClick={() => runCommand(cmd)}
                    disabled={!hardwareEnabled}
                    className={`${ACTION_CLASS} w-full`}
                  >
                    Drone Power · Bay {bay.bay_index}
                    {ackGlyph(cmd)}
                  </button>
                );
              })}
          </div>
        </div>
      </details>

      {/* ---- Cameras: opened only on request ---- */}
      <Panel title="Cameras" hint="On request">
        <div className="grid grid-cols-2 gap-1.5">
          {/* Internal first: it is the one an operator reaches for during a
              launch or a tray approach. cam1/cam2 are the only identifiers the
              stream pipeline records — DOCK_CAMERA_LABELS names them. */}
          {([2, 1] as const).map((cam) => {
            const open = openCameras.includes(cam);
            return (
              <button
                key={cam}
                type="button"
                onClick={() => onOpenCamera(cam)}
                // Pressing an open camera again does nothing on purpose. Its
                // window's X is the only way to close it — a button that opened
                // on one press and closed on the next would be a second, easily
                // mistaken dismissal path, which is the defect this replaced.
                //
                // Said with `aria-disabled` and a VISIBLE word, not `aria-pressed`
                // and a tint. `aria-pressed` announces a toggle, so a screen
                // reader would promise that pressing again un-presses; and the
                // tint that used to carry the state was about 1.1:1 against the
                // panel, well under the 3:1 a state indicator needs, with the
                // explanation in a `title` tooltip that does not exist on the
                // touch layout this sheet has.
                aria-disabled={open}
                className={`${ACTION_CLASS} w-full ${open ? 'text-foreground' : ''}`}
              >
                <Video className="size-3" aria-hidden /> {DOCK_CAMERA_LABELS[cam]}
                {open && <span className="text-status-cyan-fg">· Open</span>}
              </button>
            );
          })}
        </div>
      </Panel>

      {/* ---- Environment ---- */}
      <Panel title="Environment" hint={staleNotice ?? 'Live'}>
        {live === null ? (
          <p className="text-[10px] uppercase text-muted-foreground">No weather data received yet</p>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-1">
              <StatCard numeric label="Temp" value={formatReading(live.temp_outdoor, { digits: 1, unit: '°C' })} />
              <StatCard numeric label="Wind" value={formatReading(live.wind_speed, { digits: 1, unit: ' m/s' })} />
              <StatCard numeric label="Dir" value={formatReading(live.wind_direction, { unit: '°' })} />
              <StatCard numeric label="Rain" value={formatReading(live.rainfall, { digits: 1, unit: ' mm' })} />
            </div>
            <button
              type="button"
              onClick={() => setEnvDetailsOpen((open) => !open)}
              aria-expanded={envDetailsOpen}
              className="mt-2 text-[9px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
            >
              {envDetailsOpen ? 'Hide environment details' : 'View environment details ›'}
            </button>
            {envDetailsOpen && (
              <div className="mt-1.5 grid grid-cols-4 gap-1">
                <StatCard numeric label="Temp in" value={formatReading(live.temp_inner, { unit: '°C' })} />
                <StatCard numeric label="Temp out" value={formatReading(live.temp_outer, { unit: '°C' })} />
                <StatCard numeric label="Humidity" value={formatReading(live.humidity, { unit: '%' })} />
                <StatCard numeric label="Pressure" value={formatReading(live.pressure, { digits: 1, unit: ' kPa' })} />
                <StatCard numeric label="PM2.5" value={formatReading(live.pm25, { unit: ' µg/m³' })} />
                <StatCard numeric label="PM10" value={formatReading(live.pm10, { unit: ' µg/m³' })} />
                <StatCard numeric label="Noise" value={formatReading(live.noise, { unit: ' dB' })} />
                <StatCard numeric label="Lux" value={formatReading(live.lux)} />
              </div>
            )}
          </>
        )}
      </Panel>

      {command.failures.length > 0 && (
        <div className="rounded-lg border border-status-red/30 bg-status-red/[0.06] px-2.5 py-1.5">
          {command.failures.map(([cmd, detail]) => (
            <p key={cmd} className="truncate text-[10px] text-status-red-fg" title={detail}>
              {cmd.replace(/_/g, ' ').toUpperCase()} ✗ {detail}
            </p>
          ))}
        </div>
      )}

      {/* The camera windows themselves are NOT here. They are drawn by the COP
          page (`CopCameraWindows`), which outlives a selection change — this
          panel is unmounted the moment the operator picks a drone, and a window
          owned from here died with it. */}

      <Dialog open={confirmCmd !== null} onOpenChange={() => setConfirmCmd(null)}>
        <DialogContent className="max-w-sm border-destructive/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="size-4" strokeWidth={2} />
              {CONFIRM_DIALOG.dockTitle}
            </DialogTitle>
          </DialogHeader>
          {/* The command is NAMED, not spelled out of its MQTT method. This dialog used to
              ask "Send TURN OFF DRONES to DOCK-01?" - a question written by the protocol. */}
          <p className="text-sm text-muted-foreground">
            Send <span className="text-foreground">{confirmCmd ? DOCK_COMMAND_LABEL[confirmCmd] : ''}</span> to{' '}
            <span className="text-foreground">{callsign ?? dockId.slice(-6).toUpperCase()}</span>?
          </p>
          <p className="text-[11px] text-destructive/80">{CONFIRM_DIALOG.irreversible}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmCmd(null)}>
              {CONFIRM_DIALOG.cancel}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (confirmCmd) void command.send(confirmCmd);
                setConfirmCmd(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {CONFIRM_DIALOG.send}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
